import React, { useMemo, useState } from 'react';
import { SideBarModal } from '@/components/Modal';
import { Button, SearchableDropdown } from '@/components';
import { RepossessionRecoveryCostType } from '@/types/common';

interface MarkAsFoundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    recoveryCost: number;
    recoveryCostType?: RepossessionRecoveryCostType;
    recoveryCostTypeOther?: string;
    recoveryNotes: string;
  }) => void;
  isSubmitting?: boolean;
}

const MarkAsFoundModal: React.FC<MarkAsFoundModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const recoveryCostTypeOptions = [
    {
      value: RepossessionRecoveryCostType.TOWING_FEE,
      label: 'Towing Fee',
    },
    {
      value: RepossessionRecoveryCostType.LEGAL_FEE,
      label: 'Legal Fee',
    },
    {
      value: RepossessionRecoveryCostType.STORAGE_FEE,
      label: 'Storage Fee',
    },
    {
      value: RepossessionRecoveryCostType.VEHICLE_DAMAGE,
      label: 'Vehicle Damage',
    },
    {
      value: RepossessionRecoveryCostType.OTHER,
      label: 'Other',
    },
  ];

  const [recoveryCostInput, setRecoveryCostInput] = useState('');
  const [recoveryCostType, setRecoveryCostType] = useState<
    RepossessionRecoveryCostType | ''
  >('');
  const [recoveryCostTypeOther, setRecoveryCostTypeOther] = useState('');
  const [recoveryNotes, setRecoveryNotes] = useState('');

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  const parsedCost = recoveryCostInput ? Number(recoveryCostInput) : NaN;
  const recoveryCost = Number.isNaN(parsedCost) ? null : parsedCost;
  const shouldShowCostType =
    typeof recoveryCost === 'number' && recoveryCost > 0;
  const needsOtherCostType =
    shouldShowCostType &&
    recoveryCostType === RepossessionRecoveryCostType.OTHER;

  const handleClose = () => {
    setRecoveryCostInput('');
    setRecoveryCostType('');
    setRecoveryCostTypeOther('');
    setRecoveryNotes('');
    onClose();
  };

  const handleSubmit = () => {
    if (recoveryCost === null) return;
    onSubmit({
      recoveryCost,
      recoveryCostType: shouldShowCostType
        ? (recoveryCostType as RepossessionRecoveryCostType)
        : undefined,
      recoveryCostTypeOther: needsOtherCostType
        ? recoveryCostTypeOther.trim()
        : undefined,
      recoveryNotes: recoveryNotes.trim(),
    });
  };

  const isFormValid =
    recoveryCost !== null &&
    recoveryNotes.trim().length >= 20 &&
    (!shouldShowCostType || Boolean(recoveryCostType)) &&
    (!needsOtherCostType || recoveryCostTypeOther.trim().length > 0);

  const isRecoveryNotesInvalid =
    recoveryNotes.trim().length > 0 && recoveryNotes.trim().length < 20;

  const formatCostValue = () => {
    if (!recoveryCostInput) return '';
    const numericValue = Number(recoveryCostInput);
    if (Number.isNaN(numericValue)) return '';
    return formatter.format(numericValue);
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleClose}
      position="right"
      width="sm"
      maxHeight="screen"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Mark Vehicle as Found
          </div>
          <div className="text-xs text-gray-500">
            Confirm unit recovery and input any associated costs.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="w-full"
            variant={'primary'}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }
      className="max-w-[400px]"
    >
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Detail
      </div>
      <div className="px-6 py-4">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Recovery Cost<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={formatCostValue()}
                onChange={(event) => {
                  const digitsOnly = event.target.value.replace(/[^\d]/g, '');
                  setRecoveryCostInput(digitsOnly);
                }}
                placeholder="Rp 0"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
              <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                IDR
              </span>
            </div>
            <div className="mt-2 flex items-start gap-2 text-xs text-gray-500">
              <i className="ri-information-line mt-0.5" />
              <span>
                If recovery cost &gt; Rp 0, status will automatically change to
                PENDING_APPROVAL
              </span>
            </div>
          </div>

          {shouldShowCostType && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Cost Type<span className="text-red-500"> *</span>
              </label>
              <div className="relative">
                <SearchableDropdown
                  options={recoveryCostTypeOptions}
                  value={recoveryCostType || undefined}
                  onChange={(value) =>
                    setRecoveryCostType(value as RepossessionRecoveryCostType)
                  }
                  placeholder="e.g. Towing Fee"
                  className="w-full"
                  allowClear={false}
                />
              </div>
            </div>
          )}

          {needsOtherCostType && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Other Cost Type<span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                value={recoveryCostTypeOther}
                onChange={(event) =>
                  setRecoveryCostTypeOther(event.target.value)
                }
                placeholder="Describe other cost type"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Recovery Notes<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <textarea
                value={recoveryNotes}
                onChange={(event) => setRecoveryNotes(event.target.value)}
                placeholder="Provide recovery notes"
                rows={4}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  isRecoveryNotesInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {Math.min(recoveryNotes.length, 200)}/200
              </div>
              {isRecoveryNotesInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Minimum 20 characters
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};

export default MarkAsFoundModal;
